import en from "./locales/en";
import type { LocaleCode, Translate, TranslationDictionary, TranslationParams, TranslationValue } from "./types";

export const PRIMARY_LOCALE_STORAGE_KEY = "bustly.i18n.locale";
export const LEGACY_LOCALE_STORAGE_KEY = "openclaw.i18n.locale";
export const SUPPORTED_LOCALES: LocaleCode[] = ["en", "zh-CN"];

type ResolveInitialLocaleOptions = {
  search?: string;
  getStoredLocale?: (key: string) => string | null | undefined;
  navigatorLanguages?: readonly string[];
};

function isDictionary(value: TranslationValue | undefined): value is TranslationDictionary {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function normalizeLocale(input: string | null | undefined): LocaleCode | null {
  const normalized = input?.trim();
  if (!normalized) {
    return null;
  }
  const lowered = normalized.toLowerCase().replace("_", "-");
  if (lowered === "en" || lowered.startsWith("en-")) {
    return "en";
  }
  if (lowered === "zh" || lowered.startsWith("zh-")) {
    return "zh-CN";
  }
  return null;
}

function readLocaleFromSearch(search?: string): LocaleCode | null {
  if (!search) {
    return null;
  }
  const prefixedSearch = search.startsWith("?") ? search : `?${search}`;
  try {
    return normalizeLocale(new URLSearchParams(prefixedSearch).get("locale"));
  } catch {
    return null;
  }
}

export function resolveInitialLocale(options: ResolveInitialLocaleOptions = {}): LocaleCode {
  const fromSearch = readLocaleFromSearch(options.search);
  if (fromSearch) {
    return fromSearch;
  }

  const fromPrimaryStorage = normalizeLocale(options.getStoredLocale?.(PRIMARY_LOCALE_STORAGE_KEY));
  if (fromPrimaryStorage) {
    return fromPrimaryStorage;
  }

  const fromLegacyStorage = normalizeLocale(options.getStoredLocale?.(LEGACY_LOCALE_STORAGE_KEY));
  if (fromLegacyStorage) {
    return fromLegacyStorage;
  }

  for (const language of options.navigatorLanguages ?? []) {
    const fromNavigator = normalizeLocale(language);
    if (fromNavigator) {
      return fromNavigator;
    }
  }

  return "en";
}

function getNestedValue(messages: TranslationDictionary, key: string): string | undefined {
  let current: TranslationValue | undefined = messages;
  for (const part of key.split(".")) {
    if (!isDictionary(current)) {
      return undefined;
    }
    current = current[part];
  }
  return typeof current === "string" ? current : undefined;
}

function interpolate(template: string, params?: TranslationParams): string {
  if (!params) {
    return template;
  }
  return template.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (match, name: string) => {
    const value = params[name];
    if (value === null || value === undefined) {
      return "";
    }
    return String(value);
  });
}

export function createTranslator(locale: LocaleCode, messages: TranslationDictionary): Translate {
  return (key, params) => {
    const localValue = locale === "en" ? undefined : getNestedValue(messages, key);
    const fallbackValue = getNestedValue(en, key);
    return interpolate(localValue ?? fallbackValue ?? key, params);
  };
}

export function getBrowserInitialLocale(): LocaleCode {
  const win = typeof window === "undefined" ? undefined : window;
  return resolveInitialLocale({
    search: win?.location.search,
    getStoredLocale: (key) => {
      try {
        return win?.localStorage.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    navigatorLanguages: typeof navigator === "undefined"
      ? []
      : (navigator.languages?.length ? navigator.languages : [navigator.language]).filter(Boolean),
  });
}

export function persistLocale(locale: LocaleCode): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(PRIMARY_LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore storage failures; the in-memory provider state still updates.
  }
}

export function setDocumentLocale(locale: LocaleCode): void {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.lang = locale;
}

export async function loadLocaleMessages(locale: LocaleCode): Promise<TranslationDictionary> {
  if (locale === "zh-CN") {
    const module = await import("./locales/zh-CN");
    return module.default;
  }
  return en;
}

export { en as englishMessages };
